import type { ReactNode } from "react";
import { 
  ShoppingBag, 
  Store, 
  Truck, 
  ShieldCheck, 
  RotateCcw, 
  FileText, 
  CheckCircle2, 
  HelpCircle, 
  MapPin, 
  CreditCard, 
  Lock, 
  Scale, 
  Clock, 
  AlertCircle, 
  Award, 
  Users, 
  Sparkles,
  Phone,
  Mail,
  Building2
} from "lucide-react";

export interface StaticPageContent {
  title: string;
  subtitle?: string;
  content: ReactNode;
  cta?: { label: string; to: string };
}

export const staticPages: Record<string, StaticPageContent> = {
  about: {
    title: "About Bihari Mall",
    subtitle: "Connecting communities with neighborhood stores across Bihar.",
    content: (
      <div className="space-y-8">
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-semibold mb-3 text-primary flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Our Mission
          </h2>
          <p className="text-base text-foreground/80 leading-relaxed">
            Bihari Mall is Bihar's premier hyperlocal e-commerce marketplace operated by <strong>Biharimall Private Limited</strong>. Our platform empowers local Kirana stores, supermarkets, fresh produce vendors, and bakeries by connecting them directly with nearby shoppers through digital ordering, express delivery, and transparent rewards.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-4 font-bold">
              <Store className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Empowering Sellers</h3>
            <p className="text-sm text-muted-foreground">Giving local merchants digital tools, inventory management, and wider reach to compete in modern retail.</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center mb-4 font-bold">
              <Truck className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Express Local Delivery</h3>
            <p className="text-sm text-muted-foreground">Delivering fresh groceries, fruits, and daily essentials from trusted neighborhood stores to doorsteps in minutes.</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 text-purple-600 flex items-center justify-center mb-4 font-bold">
              <Award className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Community Rewards</h3>
            <p className="text-sm text-muted-foreground">Rewarding customers through BV cashback, discounts, and multi-level referral commissions on every order.</p>
          </div>
        </div>
      </div>
    ),
  },

  "how-it-works": {
    title: "How Bihari Mall Works",
    subtitle: "Your complete guide to shopping, selling, and delivering on Bihar's premier hyperlocal marketplace.",
    content: (
      <div className="space-y-10">
        {/* Customer Journey */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 border-b border-border pb-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">For Customers</h2>
              <p className="text-sm text-muted-foreground">Order fresh groceries & daily essentials from local stores in 4 simple steps.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-card border border-border/70 rounded-xl p-5 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center shrink-0">1</div>
                <div>
                  <h3 className="font-semibold text-base mb-1">Set Your Location & Browse Stores</h3>
                  <p className="text-sm text-muted-foreground">Enter your pincode or enable GPS location to view verified neighborhood Kirana stores, supermarkets, and specialty shops near you.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border/70 rounded-xl p-5 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center shrink-0">2</div>
                <div>
                  <h3 className="font-semibold text-base mb-1">Add Fresh Items to Cart</h3>
                  <p className="text-sm text-muted-foreground">Explore fresh fruits, vegetables, dairy, bakery, and household items. Enjoy store-specific discounts and transparent pricing.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border/70 rounded-xl p-5 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center shrink-0">3</div>
                <div>
                  <h3 className="font-semibold text-base mb-1">Choose Payment & Express Delivery</h3>
                  <p className="text-sm text-muted-foreground">Pay securely via UPI, Cards, NetBanking, Bihari Mall Wallet, or Cash on Delivery (COD). Choose doorstep delivery or store self-pickup.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border/70 rounded-xl p-5 relative overflow-hidden">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center shrink-0">4</div>
                <div>
                  <h3 className="font-semibold text-base mb-1">Live Order Tracking & BV Cashback</h3>
                  <p className="text-sm text-muted-foreground">Track your order status in real time from store preparation to delivery. Earn Business Volume (BV) cashback and referral rewards.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Merchant Journey */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 border-b border-border pb-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600">
              <Store className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">For Store Owners (Merchants)</h2>
              <p className="text-sm text-muted-foreground">Digitize your store and start serving local customers online.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-card border border-border/70 rounded-xl p-5">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center mb-3">1</div>
              <h3 className="font-semibold text-base mb-1">Register & Verify</h3>
              <p className="text-sm text-muted-foreground">Fill out store details, address, and upload basic business KYC (GST / PAN / Shop License).</p>
            </div>

            <div className="bg-card border border-border/70 rounded-xl p-5">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center mb-3">2</div>
              <h3 className="font-semibold text-base mb-1">Manage Catalog & Pricing</h3>
              <p className="text-sm text-muted-foreground">Add products, set stock quantities, MRP, selling prices, delivery radius, and minimum order limits.</p>
            </div>

            <div className="bg-card border border-border/70 rounded-xl p-5">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center mb-3">3</div>
              <h3 className="font-semibold text-base mb-1">Receive Instant Payouts</h3>
              <p className="text-sm text-muted-foreground">Pack incoming customer orders, dispatch via self-delivery or platform drivers, and receive weekly payouts.</p>
            </div>
          </div>
        </section>

        {/* Delivery Partner Journey */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 border-b border-border pb-3">
            <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-600">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">For Delivery Partners</h2>
              <p className="text-sm text-muted-foreground">Earn flexible income by delivering orders in your city.</p>
            </div>
          </div>

          <div className="bg-secondary/40 border border-border rounded-xl p-6 flex flex-col md:flex-row items-center gap-6 justify-between">
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">Join Our Delivery Network</h3>
              <p className="text-sm text-muted-foreground max-w-xl">
                Register as a delivery partner with valid vehicle documents and ID proof. Accept delivery tasks nearby, navigate easily, and earn per-order payouts with performance bonuses.
              </p>
            </div>
            <a href="/delivery-partner" className="px-5 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap text-sm">
              Learn More
            </a>
          </div>
        </section>
      </div>
    ),
  },

  privacy: {
    title: "Privacy Policy",
    subtitle: "Effective Date: July 19, 2026 • Issued by Biharimall Private Limited",
    content: (
      <div className="space-y-8 text-foreground/90">
        <div className="bg-card border border-border rounded-xl p-5 text-sm leading-relaxed text-muted-foreground">
          At <strong>Bihari Mall</strong> (a unit of <strong>Biharimall Private Limited</strong>), we value your trust and are committed to safeguarding your personal data. This Privacy Policy outlines how we collect, use, process, and protect your information when you visit our website, mobile application, or use our hyperlocal marketplace services in compliance with the Information Technology Act, 2000 and the SPDI Rules of India.
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Lock className="w-5 h-5 text-primary" /> 1. Information We Collect
          </h2>
          <p className="text-sm text-muted-foreground">We collect information to provide, personalize, and improve our hyperlocal marketplace services:</p>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li><strong>Personal Identification Data:</strong> Full Name, Email Address, Phone Number, and Delivery Address.</li>
            <li><strong>Location Data:</strong> Precise GPS coordinates (when permitted) to identify nearby registered stores, calculate delivery fees, and optimize delivery routing.</li>
            <li><strong>Account & Payment Data:</strong> User login credentials, order history, invoice records, Business Volume (BV) ledger data, and transaction IDs (payment details are processed through PCI-DSS compliant gateways like Razorpay/Stripe; we do not store CVV or full card numbers).</li>
            <li><strong>Device & Technical Data:</strong> IP Address, device identifier, browser type, operating system, and app usage metrics.</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Sparkles className="w-5 h-5 text-primary" /> 2. How We Use Your Information
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>To process, pack, and deliver your grocery and marketplace orders.</li>
            <li>To communicate order status updates via SMS, WhatsApp, push notifications, or Email.</li>
            <li>To credit Business Volume (BV) cashback, referral rewards, and process wallet transactions.</li>
            <li>To assist customer support inquiries and resolve order issues or refund claims.</li>
            <li>To detect, investigate, and prevent fraudulent transactions, fake accounts, or security incidents.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Users className="w-5 h-5 text-primary" /> 3. Data Sharing & Third Parties
          </h2>
          <p className="text-sm text-muted-foreground">We do not sell, rent, or trade your personal information. We share relevant data only with authorized parties strictly for operational purposes:</p>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li><strong>Registered Merchants & Stores:</strong> Name, phone number, and delivery items are shared with the store packing your order.</li>
            <li><strong>Delivery Partners:</strong> Name, contact number, and delivery address are shared with assigned delivery agents.</li>
            <li><strong>Payment Gateways & Banking Partners:</strong> Secure transmission of transaction amounts to authorized payment processors.</li>
            <li><strong>Legal Requirements:</strong> Disclosed only when mandated by law, court orders, or government agencies under Indian jurisdiction.</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <ShieldCheck className="w-5 h-5 text-primary" /> 4. Data Security & Storage
          </h2>
          <p className="text-sm text-muted-foreground">
            We implement robust physical, technical, and administrative safeguards including 256-bit SSL/TLS encryption for data in transit and encrypted storage for sensitive fields. Access to personal data is restricted strictly to authorized employees with confidentiality obligations.
          </p>
        </section>

        {/* Section 5 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <CheckCircle2 className="w-5 h-5 text-primary" /> 5. Your Privacy Rights & Choices
          </h2>
          <p className="text-sm text-muted-foreground">You have the right to:</p>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>Access and update your profile, delivery addresses, and mobile details via Account Settings.</li>
            <li>Opt out of promotional communications at any time.</li>
            <li>Request deletion of your account and personal data by emailing our Privacy Officer.</li>
          </ul>
        </section>

        {/* Section 6 - Grievance */}
        <section className="bg-secondary/40 border border-border rounded-xl p-5 space-y-2">
          <h3 className="font-semibold text-base text-foreground">Grievance & Privacy Officer</h3>
          <p className="text-sm text-muted-foreground">In accordance with the Information Technology Act 2000, if you have any questions or complaints regarding privacy, please contact:</p>
          <div className="text-sm font-mono space-y-1 text-foreground pt-1">
            <p><strong>Grievance Officer:</strong> Legal & Privacy Cell</p>
            <p><strong>Company:</strong> Biharimall Private Limited (Bihari Mall)</p>
            <p><strong>Address:</strong> Main Road, Boring Road, Patna, Bihar 800001, India</p>
            <p><strong>Email:</strong> privacy@biharimall.com | support@biharimall.com</p>
          </div>
        </section>
      </div>
    ),
  },

  terms: {
    title: "Terms of Service",
    subtitle: "Effective Date: July 19, 2026 • Agreement between User & Biharimall Private Limited",
    content: (
      <div className="space-y-8 text-foreground/90">
        <div className="bg-card border border-border rounded-xl p-5 text-sm leading-relaxed text-muted-foreground">
          Welcome to <strong>Bihari Mall</strong>, owned and operated by <strong>Biharimall Private Limited</strong>. By accessing or using our platform via web or mobile app, you agree to be bound by these Terms of Service. Please read them carefully before placing an order or registering a store.
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Scale className="w-5 h-5 text-primary" /> 1. Platform Marketplace Role
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Bihari Mall is an online technology marketplace that enables users to purchase goods from independent registered local stores, Kirana shops, and vendors. Bihari Mall facilitates order placement, technology infrastructure, payment routing, and delivery coordination, but the contract of sale for goods is directly between the buyer and the selling merchant.
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <CheckCircle2 className="w-5 h-5 text-primary" /> 2. Account Eligibility & Responsibilities
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>You must be at least 18 years old or under adult supervision to register an account and transact.</li>
            <li>You agree to provide true, accurate, and current information during registration.</li>
            <li>You are responsible for maintaining the confidentiality of your login credentials and OTPs.</li>
            <li>Creating multiple fake accounts, abusing referral bonuses, or indulging in fraudulent activity will result in permanent account suspension and forfeiture of rewards.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <CreditCard className="w-5 h-5 text-primary" /> 3. Pricing, Payments & Tax
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li><strong>Prices & Discounts:</strong> Product prices, MRP, and discounts are determined and updated by individual selling stores.</li>
            <li><strong>Delivery Charges & Taxes:</strong> Applicable delivery fees, packaging charges, and GST are calculated at checkout and displayed transparently before payment.</li>
            <li><strong>Payment Modes:</strong> Supported payment options include UPI, Credit/Debit Cards, NetBanking, Bihari Mall Wallet, and Cash on Delivery (COD).</li>
            <li><strong>Cash on Delivery:</strong> COD orders must be paid in full to the delivery partner upon arrival before package handover.</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Store className="w-5 h-5 text-primary" /> 4. Merchant & Product Warranties
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Selling stores are solely responsible for ensuring that items listed (especially fresh produce, food items, dairy, and groceries) are unexpired, authentic, properly packaged, and compliant with applicable food safety standards (FSSAI). Bihari Mall conducts seller verification but is not liable for merchant-side product defects.
          </p>
        </section>

        {/* Section 5 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Truck className="w-5 h-5 text-primary" /> 5. Delivery & Risk of Loss
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Delivery timelines shown on the platform are estimates based on distance, store preparation speed, and traffic conditions. Risk of loss or damage to goods passes to the customer upon successful doorstep delivery or store pickup.
          </p>
        </section>

        {/* Section 6 */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <FileText className="w-5 h-5 text-primary" /> 6. Governing Law & Dispute Resolution
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of India. Any legal dispute, claim, or proceedings arising out of or in connection with Bihari Mall shall be subject to the exclusive jurisdiction of the competent courts in <strong>Patna, Bihar, India</strong>.
          </p>
        </section>
      </div>
    ),
  },

  refund: {
    title: "Cancellation & Refund Policy",
    subtitle: "Fair, transparent policies for order cancellations, returns, and refunds.",
    content: (
      <div className="space-y-8 text-foreground/90">
        {/* Banner callout */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5 flex items-start gap-4">
          <RotateCcw className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-base text-foreground">Customer Satisfaction Guarantee</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We strive to deliver fresh groceries and accurate orders. If your order contains damaged, expired, or incorrect items, we offer quick resolution through wallet refunds or replacement.
            </p>
          </div>
        </div>

        {/* Section 1 - Cancellation */}
        <section className="space-y-4">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <Clock className="w-5 h-5 text-primary" /> 1. Order Cancellation Policy
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="font-semibold text-sm mb-2 text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Customer Cancellations
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                You can cancel an order free of cost before the store accepts and starts preparing/packing your order. Once the order is packed or dispatched with a delivery agent, cancellation may incur a compensation fee up to the order value.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="font-semibold text-sm mb-2 text-foreground flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" /> Store / Platform Cancellations
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                If a store cannot fulfill your order due to item stock unavailability, unserviceable address, or severe weather disruptions, your order will be cancelled with <strong>100% immediate refund</strong> to your payment source or wallet.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2 - Return Windows */}
        <section className="space-y-4">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <RotateCcw className="w-5 h-5 text-primary" /> 2. Return & Replacement Timeline
          </h2>

          <div className="overflow-hidden border border-border rounded-xl">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted text-muted-foreground font-semibold border-b">
                <tr>
                  <th className="p-3.5">Category</th>
                  <th className="p-3.5">Return Window</th>
                  <th className="p-3.5">Eligible Conditions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-xs md:text-sm">
                <tr>
                  <td className="p-3.5 font-medium">Fresh Fruits & Vegetables</td>
                  <td className="p-3.5 text-amber-600 dark:text-amber-400 font-semibold">Within 2 Hours of Delivery</td>
                  <td className="p-3.5 text-muted-foreground">Spoiled, rotten, damaged, or poor quality upon arrival. Photo required.</td>
                </tr>
                <tr>
                  <td className="p-3.5 font-medium">Dairy, Eggs, Meat & Bakery</td>
                  <td className="p-3.5 text-amber-600 dark:text-amber-400 font-semibold">Within 2 Hours of Delivery</td>
                  <td className="p-3.5 text-muted-foreground">Expired, unsealed, damaged packaging, or sour milk. Photo required.</td>
                </tr>
                <tr>
                  <td className="p-3.5 font-medium">Packaged Grocery & Staples</td>
                  <td className="p-3.5 text-emerald-600 dark:text-emerald-400 font-semibold">Within 24 Hours of Delivery</td>
                  <td className="p-3.5 text-muted-foreground">Past expiry date, torn seal, wrong item, or missing quantities.</td>
                </tr>
                <tr>
                  <td className="p-3.5 font-medium">Personal Care & Household</td>
                  <td className="p-3.5 text-emerald-600 dark:text-emerald-400 font-semibold">Within 24 Hours of Delivery</td>
                  <td className="p-3.5 text-muted-foreground">Unopened original packaging, defective, or incorrect product delivered.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 3 - Refund Processing */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <CreditCard className="w-5 h-5 text-primary" /> 3. Refund Execution & Timelines
          </h2>
          <p className="text-sm text-muted-foreground">Once a refund claim is approved by our support team:</p>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li><strong>Bihari Mall Wallet Refund:</strong> Credited <strong>instantly</strong> upon approval. Can be used for future orders immediately.</li>
            <li><strong>Online Payment (UPI / Credit Card / Debit Card / NetBanking):</strong> Processed within <strong>3 to 5 business days</strong> back to the original bank source.</li>
            <li><strong>Cash on Delivery (COD) Orders:</strong> Refunds for COD orders are credited directly to your Bihari Mall User Wallet or via UPI transfer to your bank account.</li>
          </ul>
        </section>

        {/* Section 4 - Non-refundable */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2 border-b pb-2">
            <AlertCircle className="w-5 h-5 text-rose-500" /> 4. Non-Refundable Scenarios
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
            <li>Claims submitted after the designated return window (2 hours for fresh/dairy, 24 hours for packaged items).</li>
            <li>Items damaged due to improper storage or handling by the customer after delivery.</li>
            <li>Items where original tags, seals, or packaging have been intentionally tampered with or discarded.</li>
            <li>Incorrect delivery address provided by the customer leading to non-delivery or perishable spoilage.</li>
          </ul>
        </section>

        {/* Section 5 - How to apply */}
        <section className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h3 className="font-semibold text-base text-foreground flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" /> How to Initiate a Return or Refund Request
          </h3>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground">
            <li>Go to <strong>My Account</strong> &rarr; <strong>Orders</strong> on Bihari Mall.</li>
            <li>Select the order containing the damaged or missing item.</li>
            <li>Click <strong>Request Support / Refund</strong> and upload a clear photo of the issue.</li>
            <li>Our support team will verify and process your refund/replacement within 24 hours.</li>
          </ol>
          <div className="pt-2 text-xs text-muted-foreground border-t border-border mt-3 flex items-center gap-4">
            <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> support@biharimall.com</span>
            <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> +91 98765 43210</span>
          </div>
        </section>
      </div>
    ),
  },

  "become-a-seller": {
    title: "Become a Seller on Bihari Mall",
    subtitle: "Expand your shop digitally and connect with neighboring shoppers across Bihar.",
    content: (
      <div className="space-y-6">
        <p className="text-base text-muted-foreground">
          Bihari Mall gives local retailers, Kirana merchants, fruit & vegetable vendors, and bakeries the digital platform to sell online, manage stock, set custom delivery zones, and boost sales.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border rounded-xl bg-card">
            <h3 className="font-semibold text-base mb-1">Zero Setup Commission</h3>
            <p className="text-xs text-muted-foreground">Register your shop with transparent terms and start receiving orders from nearby localities.</p>
          </div>
          <div className="p-4 border rounded-xl bg-card">
            <h3 className="font-semibold text-base mb-1">Flexible Delivery Options</h3>
            <p className="text-xs text-muted-foreground">Fulfill orders using your own shop staff or utilize Bihari Mall's delivery partner network.</p>
          </div>
        </div>
      </div>
    ),
    cta: { label: "Register Your Store Now", to: "/auth/register-store" },
  },

  "delivery-partner": {
    title: "Delivery Partner Program",
    subtitle: "Earn competitive payouts delivering orders in your city.",
    content: (
      <div className="space-y-6">
        <p className="text-base text-muted-foreground">
          Join Bihari Mall's fleet of delivery partners. Pick up packed orders from local stores and deliver them to nearby customers with flexible working hours.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-4 border rounded-xl bg-card">
            <h3 className="font-bold text-lg text-primary">Flexible Hours</h3>
            <p className="text-xs text-muted-foreground mt-1">Work full-time or part-time based on your availability.</p>
          </div>
          <div className="p-4 border rounded-xl bg-card">
            <h3 className="font-bold text-lg text-primary">Weekly Payouts</h3>
            <p className="text-xs text-muted-foreground mt-1">Get earnings transferred directly to your bank account every week.</p>
          </div>
          <div className="p-4 border rounded-xl bg-card">
            <h3 className="font-bold text-lg text-primary">Order Incentives</h3>
            <p className="text-xs text-muted-foreground mt-1">Earn additional bonuses during peak hours and holidays.</p>
          </div>
        </div>
      </div>
    ),
    cta: { label: "Apply as Delivery Partner", to: "/auth/register-delivery" },
  },

  help: {
    title: "Help & Customer Support",
    subtitle: "We are here to assist you with your orders, account, or store registration.",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 border rounded-xl bg-card flex items-start gap-4">
            <Mail className="w-6 h-6 text-primary shrink-0" />
            <div>
              <h3 className="font-semibold text-base">Email Support</h3>
              <p className="text-sm text-muted-foreground mt-1">Send your query with Order ID to:</p>
              <p className="text-sm font-semibold text-foreground mt-1">support@biharimall.com</p>
            </div>
          </div>
          <div className="p-5 border rounded-xl bg-card flex items-start gap-4">
            <Phone className="w-6 h-6 text-primary shrink-0" />
            <div>
              <h3 className="font-semibold text-base">Helpline Number</h3>
              <p className="text-sm text-muted-foreground mt-1">Mon – Sat, 9:00 AM – 8:00 PM IST:</p>
              <p className="text-sm font-semibold text-foreground mt-1">+91 98765 43210</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  contact: {
    title: "Contact Us",
    subtitle: "Biharimall Private Limited",
    content: (
      <div className="space-y-6">
        <div className="p-6 border rounded-xl bg-card space-y-4 text-sm">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-base text-foreground">Registered Office Address</h3>
              <p className="text-muted-foreground mt-1">
                Biharimall Private Limited<br />
                Main Road, Boring Road, Patna, Bihar 800001, India
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t">
            <Mail className="w-5 h-5 text-primary shrink-0" />
            <span><strong>Corporate Email:</strong> hello@biharimall.com</span>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t">
            <Phone className="w-5 h-5 text-primary shrink-0" />
            <span><strong>Customer Helpline:</strong> +91 98765 43210</span>
          </div>
        </div>
      </div>
    ),
  },
};

export const staticPagePaths: Record<string, string> = {
  "/about": "about",
  "/how-it-works": "how-it-works",
  "/become-a-seller": "become-a-seller",
  "/delivery-partner": "delivery-partner",
  "/help": "help",
  "/contact": "contact",
  "/privacy": "privacy",
  "/terms": "terms",
  "/refund": "refund",
};
